import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2 } from 'lucide-react';
import { MultiImageUpload } from '@/components/shared/ImageUpload';

export const ProductEditForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: product.title || '',
    description: product.description || '',
    price: product.price || '',
    original_price: product.original_price || '',
    stock: product.stock || 0,
    category_id: product.category_id || '',
    condition: product.condition || 'new'
  });
  const [uploadedImages, setUploadedImages] = useState(product.images || []);
  const [imageUrl, setImageUrl] = useState('');

  const handleAddImageUrl = () => {
    if (!imageUrl.trim()) return;
    const urls = imageUrl.split(',').map(url => url.trim()).filter(url => url.length > 0);
    const validUrls = urls.filter(url => url.startsWith('http://') || url.startsWith('https://'));
    if (validUrls.length === 0) return;
    const newImages = [...uploadedImages, ...validUrls].slice(0, 5);
    setUploadedImages(newImages);
    setImageUrl('');
  };

  const handleSubmit = () => {
    onSave({
      title: formData.title,
      description: formData.description,
      price: parseFloat(formData.price),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      stock: parseInt(formData.stock),
      category_id: formData.category_id,
      condition: formData.condition,
      images: uploadedImages
    });
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4 bg-gray-50">
        <Label className="font-semibold text-[#003B73] mb-3 block">Produktbilder</Label>
        {uploadedImages.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Nuvarande bilder (klicka för att ta bort):</p>
            <div className="flex flex-wrap gap-2">
              {uploadedImages.map((img, index) => (
                <div key={index} className="relative group cursor-pointer" onClick={() => setUploadedImages(uploadedImages.filter((_, i) => i !== index))}>
                  <img src={img} alt="" className="w-20 h-20 object-cover rounded border" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                    <Trash2 className="h-5 w-5 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <MultiImageUpload images={uploadedImages} onImagesChange={setUploadedImages} maxImages={5} folder="products" />
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Label className="text-sm text-gray-600 mb-2 block">Eller lägg till bild via URL:</Label>
          <div className="flex gap-2">
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://example.com/bild.jpg" className="flex-1" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImageUrl())} />
            <Button type="button" variant="outline" onClick={handleAddImageUrl} disabled={uploadedImages.length >= 5}>Lägg till</Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">{uploadedImages.length}/5 bilder. Separera flera URL:er med komma.</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Titel</Label>
          <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="mt-1" />
        </div>
        <div className="col-span-2">
          <Label>Beskrivning</Label>
          <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1" rows={3} />
        </div>
        <div>
          <Label>Pris (kr)</Label>
          <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>Ordinarie pris (valfritt)</Label>
          <Input type="number" value={formData.original_price} onChange={(e) => setFormData({ ...formData, original_price: e.target.value })} className="mt-1" placeholder="Lämna tomt om ej kampanj" />
        </div>
        <div>
          <Label>Lager</Label>
          <Input type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>Kategori-ID</Label>
          <Input value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>Skick</Label>
          <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Välj skick" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Ny vara</SelectItem>
              <SelectItem value="demo">Demoex</SelectItem>
              <SelectItem value="defect">Defekt</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>Avbryt</Button>
        <Button className="bg-[#003B73]" onClick={handleSubmit}>Spara ändringar</Button>
      </div>
    </div>
  );
};
